export type FetchRetryOptions = {
  retries?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  retryOn?: (response: Response) => boolean;
};

const DEFAULT_RETRY_ON = (response: Response) =>
  response.status === 429 || response.status >= 500;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit,
  options: FetchRetryOptions = {},
): Promise<Response> {
  const retries = Math.max(0, options.retries ?? 2);
  const baseDelayMs = options.baseDelayMs ?? 300;
  const maxDelayMs = options.maxDelayMs ?? 4_000;
  const retryOn = options.retryOn ?? DEFAULT_RETRY_ON;

  let attempt = 0;
  let lastError: unknown;

  while (attempt <= retries) {
    try {
      const response = await fetch(input, init);
      if (!retryOn(response) || attempt === retries) {
        return response;
      }
      await response.body?.cancel?.();
    } catch (error) {
      lastError = error;
      if (attempt === retries) {
        throw error;
      }
    }

    const delay = Math.min(maxDelayMs, baseDelayMs * 2 ** attempt);
    await sleep(delay);
    attempt += 1;
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("fetchWithRetry failed without response");
}
